import { useState } from "react";
import { Mail, Eye, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function PreviewModal({ data, onClose }) {
  const [tab, setTab] = useState(data.player_html ? "player" : "organiser");
  const html = tab === "player" ? data.player_html : data.organiser_html;
  return (
    <div className="fixed inset-0 z-[95] bg-black/80 flex items-center justify-center p-3" onClick={onClose} data-testid="post-summary-preview-modal">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border-2 border-[#CCFF00] flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#262626]">
          <div className="flex gap-2">
            {["player", "organiser"].map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)} disabled={t === "player" && !data.player_html} data-testid={`post-summary-tab-${t}`}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border transition-colors disabled:opacity-40 ${tab === t ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#B0B0B0] hover:text-white"}`}>
                {t === "player" ? "Player recap" : "Organiser summary"}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} data-testid="post-summary-preview-close" className="p-1 border border-[#262626] text-[#E0E0E0] hover:text-white" aria-label="Close"><X className="w-4 h-4" /></button>
        </header>
        <iframe title="Email preview" srcDoc={html || "<p style='color:#999;font-family:Arial'>No players with games yet — nothing to preview.</p>"} className="w-full flex-1 min-h-[60vh] bg-[#0A0A0A]" data-testid="post-summary-preview-frame" />
      </div>
    </div>
  );
}

export function PostFestivalEmailsCard({ festival, onChange }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const suppressed = !!festival?.post_summary_suppressed;
  const sentAt = festival?.post_summary_sent_at;

  const toggle = async () => {
    setBusy(true);
    try {
      await api.put(`/festival/${festival.id}/post-summary`, { suppressed: !suppressed });
      toast.success(suppressed ? "Post-festival emails will be sent" : "Post-festival emails suppressed");
      await onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };

  const openPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/festival/${festival.id}/post-summary/preview`);
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-[#262626] bg-[#0F0F0F] p-4" data-testid="post-summary-card">
      <div className="label-mono text-[10px] text-[#CCFF00] mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> POST-FESTIVAL EMAILS</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 max-w-xl">
          <div className="text-sm text-white">Send recap emails when the festival ends</div>
          <div className="text-[11px] text-[#B0B0B0] mt-1">
            Players get a fun personal recap with an AI-written paragraph, and you get an operational summary. Sent automatically within an hour of the end time.
          </div>
          <div className="text-[11px] mt-2" data-testid="post-summary-status">
            {sentAt
              ? <span className="text-[#CCFF00]">Sent {new Date(sentAt).toLocaleString()}</span>
              : suppressed ? <span className="text-[#FF4500]">Suppressed — no emails will be sent</span>
                : <span className="text-[#E0E0E0]">Scheduled for festival end</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={openPreview} disabled={busy} data-testid="post-summary-preview-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#262626] text-[#E0E0E0] hover:border-white hover:text-white transition-colors disabled:opacity-40">
            <Eye className="w-3.5 h-3.5" /> {busy && !preview ? "Loading…" : "Preview"}
          </button>
          <button type="button" role="switch" aria-checked={!suppressed} onClick={toggle} disabled={busy || !!sentAt} data-testid="post-summary-toggle"
            className={`px-3 py-2 text-[10px] uppercase tracking-widest border font-bold transition-colors disabled:opacity-40 ${!suppressed ? "border-[#CCFF00] bg-[#CCFF00] text-black" : "border-[#FF4500] text-[#FF4500]"}`}>
            {!suppressed ? "On" : "Off"}
          </button>
        </div>
      </div>
      {preview && <PreviewModal data={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

export default PostFestivalEmailsCard;
